/**
 * The nodal view's layout numbers, in world units unless they say pixels: the forces (link
 * lengths and stiffness, pulls toward the centre and the categories' corners, friction, the
 * push between nodes and its tree), when the push moves to workers and how long warming up may
 * take, how new nodes are seeded, the zoom, node sizes, group rims and names, gliding to others'
 * positions, and the 3D camera.
 */

/** From this many visible nodes, the push between them is computed in a worker. */
export const WORKER_FROM = 3000;

/** How long {@link SceneLayout.warm} may compute before letting the layout settle on screen. */
export const WARM_BUDGET_MS = 400;

/** Preferred length of some link kinds, in world units (at spacing 1): hierarchies kept tight. */
export const LINK_LENGTH: Record<string, number> = { inherits: 58, implements: 66, uses: 88, depends: 72 };

/** Preferred length of the other link kinds, in world units (at spacing 1). */
export const PLAIN_LINK_LENGTH = 88;

/** How far from the middle each category's corner is (at spacing 1). */
export const CLUSTER_RADIUS = 190;

/** Where nodes in no category, or in several, gather (at spacing 1): near the middle. */
export const CENTRE: [number, number] = [0, -40];

/** Room left between groups, and between a group and the nodes beside it (at spacing 1). */
export const GROUP_GAP = 30;

/** The narrowest and widest {@link SceneData.spacing} the layout accepts. */
export const SPACING_RANGE = { min: 0.5, max: 4 } as const;

/** Beyond this distance (at spacing 1) two nodes do not push each other. */
export const PUSH_REACH = 500;

/** A box is taken whole when its size is less than this share of its distance. */
export const PUSH_THETA = 0.7;

/** A box with this many nodes or fewer is not split. */
export const PUSH_LEAF = 12;

/** Boxes are not split deeper than this (many nodes at the same spot stay in one box). */
export const PUSH_MAX_DEPTH = 24;

/** How hard two nodes push each other at distance 1 (at spacing 1): the push is this / distance². */
export const PUSH_STRENGTH = 2600;

/** From this many new nodes at once, they are seeded; fewer just appear near their corner. */
export const SEED_FROM = 60;

/** The room one node takes in a disc, in world units (at spacing 1): about a link's length. */
export const NODE_ROOM = 46;

/**
 * The zoom limits. Zoomed far out, a big project fits on screen (names hide below a zoom of
 * about 0.6, see `windows/graph/picture.ts`, so the picture stays readable).
 */
export const ZOOM = { min: 0.04, max: 3, fitMax: 2.2 } as const;

/** The least radius of a node showing a picture: big enough to recognise it. */
export const PICTURE_RADIUS = 16;

/** The radius of a node with no link. */
export const NODE_RADIUS = 5.2;

/**
 * Room, in world units, between a group's circle and what it contains. Nodes are counted a bit
 * bigger than their world radius, because zoomed out they are drawn bigger than the zoom alone.
 */
export const GROUP_PAD = 16;

/** With more groups than this, a group whose circle is under {@link GROUP_LABEL_MIN_R} is drawn without its name. */
export const NAMES_ALWAYS = 60;

/** Below this radius on screen (pixels), a group's circle may be drawn without its name. */
export const GROUP_LABEL_MIN_R = 22;

/** How close (in pixels) to a group's rim the pointer must be to grab the group. */
export const RIM_GRAB_PX = 6;

/** How quickly a node glides to its shared place: about two thirds of the way in this many ms. */
export const FOLLOW_GLIDE_MS = 90;

/** The shortest a bundle is drawn, in pixels (it reaches into its groups if needed). */
export const MIN_BUNDLE_PX = 90;

/** How long a {@link SceneContent.hint} waits for its node, in milliseconds. */
export const HINT_LIFETIME_MS = 15_000;

/** The most an item moves in one step, so a big overlap is resolved over several steps. */
export const GROUP_MAX_SHIFT = 6;

/** The most the camera tilts, just short of looking straight down, so the view never flips. */
export const MAX_PITCH = Math.PI / 2 - 0.05;

/** The closest a point can come to the eye, as a share of `distance` (avoids dividing by zero). */
export const CAMERA_NEAREST = 0.85;

/** At most this many Web Workers share the push between nodes (one core is left to the page). */
export const MAX_WORKERS = 4;

/** How hard a link pulls its two nodes back to its length, per step. */
export const LINK_STIFFNESS = 0.06;

/** How much two linked nodes may overlap their rims before the link pushes them apart (world units). */
export const LINK_OVERLAP = 10;

/** How hard a node is pulled toward the centre (no category, or several), per step. */
export const CENTRE_PULL = 0.008;

/** How hard a node of one category is pulled toward that category's corner, per step. */
export const CORNER_PULL = 0.012;

/** How hard a node is pulled back to the middle depth in 3D, per step. */
export const DEPTH_PULL = 0.008;

/** How much of its speed a node keeps from one step to the next. */
export const FRICTION = 0.8;

/** The fastest a node moves in one step (world units). */
export const MAX_SPEED = 20;

/** How far from its corner a node whose place was lost is put back, at random (world units). */
export const LOST_SPREAD = 40;

/** How hard overlapping groups push each other apart, per step. */
export const GROUP_PUSH = 0.25;

/** The most steps warming up takes (within {@link WARM_BUDGET_MS}). */
export const WARM_STEPS = 320;

/** How much energy the layout keeps each warming step. */
export const WARM_COOLING = 0.985;
